import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getChainPublicApi, getMainPrivateApi } from 'api/apiCoreSdk';
import Button from 'components/basic/Button/Button';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import TextInputLabeled from 'components/basic/inputs/TextInputLabeled';
import LinkClickable from 'components/basic/Link/LinkClickable';
import { KATANA_PERPS } from 'config/constants';
import {
  clearBankrollCode,
  setBankrollCode,
} from 'reduxState/currentUserData/userBankroll';
import { setReferralCode } from 'reduxState/currentUserData/userReferral';
import { staticDispatch } from 'reduxState/store';
import { useSelector } from 'reduxState/storeCore';
import { showWalletModal } from 'reduxState/wallet/walletDetailsReducer';

import BankrollModalLayout from '../bankroll/BankrollModalLayout';

import { fetchReferralCodeValidityAndShowToasts } from './referralUtils';

const Heading = styled.div`
  color: var(--white);
  font-size: var(--medium);
  font-style: normal;
  line-height: normal;
`;

const MessageWrap = styled.div`
  padding: 16px 0;
  color: var(--gray4c);
  font-size: var(--small);
  font-style: normal;
  line-height: normal;
`;

const AlreadyAccessWrap = styled(MessageWrap)`
  padding: 0;
`;

const QrIcon = styled(MaterialSymbol)`
  vertical-align: middle;
`;

const FadeIn = styled.div`
  animation: fadeIn 0.3s ease-in-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export function WelcomeCodeRequiredModal({
  devFakeReferralCode,
}: {
  devFakeReferralCode?: boolean;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);

  const unlockModalOpen = useSelector(
    state => state.walletDetail.unlockModalOpen
  );
  const bankrollCodeToApply = useSelector(
    state => state.currentUserData.userBankroll.bankrollCodeToApply
  );
  const referralCode = useSelector(
    state => state.currentUserData.userReferral.referralCode
  );
  const [referralCodeValidated, setReferralCodeValidated] = useState(false);
  // useSelector(state => state.currentUserData.userReferral.referralCodeValidated);

  const [processing, setProcessing] = useState(false);
  const [newAccessCode, setNewAccessCode] = useState('');
  const [newIsInvalid, setNewIsInvalid] = useState(false);

  useEffect(() => {
    if (referralCode) {
      fetchReferralCodeValidityAndShowToasts(referralCode).then(
        setReferralCodeValidated
      );
    }
  }, [referralCode]);

  const onChangeCode = (code: string) => {
    setNewAccessCode(code);
    if (newIsInvalid) {
      setNewIsInvalid(false);
    }
  };

  if (bankrollCodeToApply || unlockModalOpen) {
    return null;
  }

  const checkReferralCode = (code: string) =>
    getChainPublicApi()
      .getPublicReferralCodeValidity(code)
      .then(response => response.isValid)
      .catch(error => {
        console.warn(error);
        return false;
      });

  const handleUnlockWithReferral = async () => {
    if (!referralCode) {
      return; // Not accessible
    }
    setProcessing(true);
    setNewIsInvalid(false);

    const referralCodeValid = await checkReferralCode(referralCode);
    if (referralCodeValid) {
      staticDispatch(showWalletModal());
    } else {
      // No valid codes found, show error
      setNewIsInvalid(true);
      // Remove codes from storage
      staticDispatch(setReferralCode(undefined));
    }
    setProcessing(false);
  };

  const handleUnlockWithNewCode = async () => {
    setProcessing(true);
    setNewIsInvalid(false);
    const referralCodeValid = await getChainPublicApi()
      .getPublicReferralCodeValidity(newAccessCode)
      .then(response => response.isValid)
      .catch(error => {
        console.warn(error);
        return false;
      });
    if (referralCodeValid) {
      staticDispatch(setReferralCode(newAccessCode));
      staticDispatch(showWalletModal());
      setProcessing(false);
      return;
    }
    const welcomeBonusResult = await getMainPrivateApi()
      .getWelcomeBonus(newAccessCode)
      .then(response => response.welcomeBonus)
      .catch(error => {
        console.warn(error);
        return null;
      });
    if (welcomeBonusResult?.canRedeem) {
      staticDispatch(setBankrollCode(newAccessCode, 'welcomePoints'));
      staticDispatch(showWalletModal());
      setProcessing(false);
      return;
    }

    // No valid codes found, show error
    setNewIsInvalid(true);
    setProcessing(false);

    // Remove codes from storage
    staticDispatch(setReferralCode(undefined));
    staticDispatch(clearBankrollCode());
  };

  const isChecking = !!referralCode && !referralCodeValidated;

  return (
    <BankrollModalLayout
      quantity={undefined}
      onClose={undefined}
      loading={isChecking}
    >
      {isChecking ? null : (
        <FadeIn>
          {referralCode || devFakeReferralCode ? (
            <>
              <Heading>Welcome to {KATANA_PERPS}!</Heading>
              <MessageWrap>
                Connect your wallet to trade on Katana Perps. Share your
                referral code with others to earn points and a percentage of
                their trading fees.
              </MessageWrap>
              <div>
                <Button
                  inline={!isMobile}
                  disabled={processing}
                  onClick={handleUnlockWithReferral}
                >
                  Connect Wallet
                </Button>
              </div>
            </>
          ) : (
            <>
              <Heading>Welcome to {KATANA_PERPS}!</Heading>
              <form
                onSubmit={event => {
                  event.preventDefault();
                  handleUnlockWithNewCode();
                }}
              >
                <MessageWrap>
                  Enter a referral code or welcome bonus code, then connect your
                  wallet to access Katana Perps. Share your referral code with
                  others to earn points and a percentage of their trading fees.
                </MessageWrap>
                <TextInputLabeled
                  placeholder="Access Code"
                  required
                  label=""
                  disabled={processing}
                  onChange={onChangeCode}
                  value={newAccessCode}
                  error={newIsInvalid ? 'Invalid access code' : undefined}
                />
                {!newIsInvalid && <div style={{ height: 16 }} />}
                <Button
                  inline={!isMobile}
                  submit
                  disabled={processing || !newAccessCode}
                >
                  Connect Wallet
                </Button>
              </form>
              <br />
              <AlreadyAccessWrap>
                Already have access?{' '}
                <LinkClickable
                  onClick={() => staticDispatch(showWalletModal())}
                  color="gray"
                  $underline
                  disabled={processing}
                >
                  Connect here
                </LinkClickable>
              </AlreadyAccessWrap>
              {isMobile && (
                <div style={{ paddingTop: 16 }}>
                  <LinkClickable
                    onClick={() =>
                      staticDispatch(
                        showWalletModal({
                          preSelect: {
                            remember: true,
                            walletType: 'qrSynced',
                          },
                        })
                      )
                    }
                    color="gray"
                    $alignCenter
                  >
                    <QrIcon icon="qr_code_scanner" fontSize="small" />
                    &nbsp;&nbsp;
                    {KATANA_PERPS} Connect
                  </LinkClickable>
                </div>
              )}
            </>
          )}
        </FadeIn>
      )}
    </BankrollModalLayout>
  );
}
